import React, { useState, useMemo, useCallback } from 'react';
import {
  Table, Tag, Space, Button, Card, Form, Input, Select,
  Typography, Modal, message, Tooltip
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, PlusOutlined,
  ClockCircleOutlined, SyncOutlined, CheckCircleOutlined, MinusCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;
const { Option } = Select;

// ══════════════════════════════════════════════
//  常量映射（与工单管理页一致）
// ══════════════════════════════════════════════

const STATUS_MAP = {
  pending:    { label: '待处理',   color: 'blue',    icon: <ClockCircleOutlined /> },
  processing: { label: '处理中',   color: 'orange',  icon: <SyncOutlined spin /> },
  resolved:   { label: '已解决',   color: 'green',   icon: <CheckCircleOutlined /> },
  closed:     { label: '已关闭',   color: 'default', icon: <MinusCircleOutlined /> },
};

const PRIORITY_MAP = {
  low:    { label: '低',   color: 'default' },
  normal: { label: '普通', color: 'blue' },
  high:   { label: '高',   color: 'orange' },
  urgent: { label: '紧急', color: 'red' },
};

const CATEGORY_MAP = {
  techSupport: { label: '技术对接', color: 'purple' },
  bug:         { label: 'BUG',      color: 'red' },
  requirement: { label: '需求',     color: 'cyan' },
  other:       { label: '其他',     color: 'default' },
};

// ══════════════════════════════════════════════
//  Mock 数据（该企业工单）
//  TODO: 联调时替换为接口数据，按 enterprise_id 查询
// ══════════════════════════════════════════════

const MOCK_TICKETS = [
  { key: '1', ticketNo: 'TK202607270001', title: 'test-SDK初始化接口返回错误码1001',
    enterpriseId: 'test-ent-001', enterpriseName: 'test-深圳智创科技有限公司',
    category: 'techSupport', priority: 'urgent', status: 'pending',
    handlerName: null, handlerId: null, handlerAccount: null,
    description: '调用SDK初始化接口时返回错误码1001，环境为iOS 18，SDK版本v3.1.0',
    createdAt: '2026-07-27 09:15:00', updatedAt: '2026-07-27 09:15:00',
  },
  { key: '2', ticketNo: 'TK202607270002', title: 'test-固件OTA升级失败排查',
    enterpriseId: 'test-ent-001', enterpriseName: 'test-深圳智创科技有限公司',
    category: 'bug', priority: 'high', status: 'processing',
    handlerName: '李客服', handlerId: 'user-01', handlerAccount: 'lifuwu',
    description: '设备型号X200固件v2.3升级到v2.5后反复重启',
    createdAt: '2026-07-27 08:30:00', updatedAt: '2026-07-27 10:00:00',
  },
  { key: '3', ticketNo: 'TK202607260004', title: 'test-App OEM定制需要新增品牌配置页',
    enterpriseId: 'test-ent-001', enterpriseName: 'test-深圳智创科技有限公司',
    category: 'requirement', priority: 'normal', status: 'resolved',
    handlerName: '王客服', handlerId: 'user-02', handlerAccount: 'wangyuan',
    description: '希望在App OEM定制中增加一个品牌配置页面，用于设置Logo和主题色',
    createdAt: '2026-07-26 16:00:00', updatedAt: '2026-07-27 11:00:00',
  },
  { key: '4', ticketNo: 'TK202607250005', title: 'test-云平台私有化部署环境确认',
    enterpriseId: 'test-ent-001', enterpriseName: 'test-深圳智创科技有限公司',
    category: 'techSupport', priority: 'normal', status: 'closed',
    handlerName: '张客服', handlerId: 'user-03', handlerAccount: 'zhangwei',
    description: '确认私有化部署的服务器配置和环境要求',
    createdAt: '2026-07-25 14:20:00', updatedAt: '2026-07-26 09:00:00',
  },
  { key: '5', ticketNo: 'TK202607240006', title: 'test-批量设备管理接口优化咨询',
    enterpriseId: 'test-ent-001', enterpriseName: 'test-深圳智创科技有限公司',
    category: 'requirement', priority: 'low', status: 'pending',
    handlerName: null, handlerId: null, handlerAccount: null,
    description: '需要支持一次性导入1000+设备的批量管理功能',
    createdAt: '2026-07-24 11:00:00', updatedAt: '2026-07-24 11:00:00',
  },
  { key: '6', ticketNo: 'TK202607230007', title: 'test-增值服务套餐配置咨询',
    enterpriseId: 'test-ent-001', enterpriseName: 'test-深圳智创科技有限公司',
    category: 'other', priority: 'low', status: 'resolved',
    handlerName: '王客服', handlerId: 'user-02', handlerAccount: 'wangyuan',
    description: '咨询如何配置设备的增值服务套餐',
    createdAt: '2026-07-23 13:00:00', updatedAt: '2026-07-24 16:00:00',
  },
];

// 可用客服（供分配选择）
const MOCK_AGENTS = [
  { id: 'user-01', name: '李客服', account: 'lifuwu' },
  { id: 'user-02', name: '王客服', account: 'wangyuan' },
  { id: 'user-03', name: '张客服', account: 'zhangwei' },
  { id: 'user-04', name: '赵客服', account: 'zhaoyun' },
  { id: 'user-05', name: '陈客服', account: 'chenli' },
];

const CURRENT_AGENT = { id: 'user-99', name: '当前客服', account: 'dangqian' };

const STATUS_ORDER = { pending: 0, processing: 1, resolved: 2, closed: 3 };
const PRIORITY_ORDER = { urgent: 0, high: 1, normal: 2, low: 3 };

// ══════════════════════════════════════════════
//  主组件：客户工单 Tab
// ══════════════════════════════════════════════

const CustomerTicketsTab = ({ enterpriseId, isArchived }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({});
  const [tickets, setTickets] = useState(MOCK_TICKETS);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignTicket, setAssignTicket] = useState(null);

  const readOnly = !!isArchived;

  const filteredTickets = useMemo(() => {
    let list = [...tickets];
    if (enterpriseId) {
      list = list.filter((t) => t.enterpriseId === enterpriseId);
    }
    const f = filters;
    if (f.status && f.status !== 'all') {
      list = list.filter((t) => t.status === f.status);
    }
    if (f.category && f.category !== 'all') {
      list = list.filter((t) => t.category === f.category);
    }
    if (f.priority && f.priority !== 'all') {
      list = list.filter((t) => t.priority === f.priority);
    }
    if (f.handlerId) {
      list = list.filter((t) => t.handlerId === f.handlerId);
    }
    if (f.keyword) {
      const kw = f.keyword.toLowerCase();
      list = list.filter((t) =>
        t.ticketNo.toLowerCase().includes(kw) ||
        t.title.toLowerCase().includes(kw)
      );
    }
    list.sort((a, b) => {
      const sa = STATUS_ORDER[a.status] ?? 99;
      const sb = STATUS_ORDER[b.status] ?? 99;
      if (sa !== sb) return sa - sb;
      const pa = PRIORITY_ORDER[a.priority] ?? 99;
      const pb = PRIORITY_ORDER[b.priority] ?? 99;
      if (pa !== pb) return pa - pb;
      return a.createdAt.localeCompare(b.createdAt);
    });
    return list;
  }, [tickets, filters, enterpriseId]);

  const handlerOptions = useMemo(() => {
    const set = new Map();
    tickets.forEach((t) => {
      if (t.handlerId && t.handlerName) set.set(t.handlerId, { name: t.handlerName, account: t.handlerAccount });
    });
    set.set(CURRENT_AGENT.id, { name: CURRENT_AGENT.name, account: CURRENT_AGENT.account });
    return Array.from(set, ([id, v]) => ({ value: id, label: `${v.name}  ${v.account}` }));
  }, [tickets]);

  const handleSearch = useCallback(() => {
    setFilters(form.getFieldsValue());
  }, [form]);

  const handleReset = useCallback(() => {
    form.resetFields();
    setFilters({});
  }, [form]);

  const handleClaim = useCallback((record) => {
    Modal.confirm({
      title: '确认领取该工单？',
      onOk: () => {
        setTickets((prev) => prev.map((t) =>
          t.key === record.key
            ? { ...t, status: 'processing', handlerId: CURRENT_AGENT.id, handlerName: CURRENT_AGENT.name, handlerAccount: CURRENT_AGENT.account, updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19) }
            : t
        ));
        message.success('已领取，工单状态更新为处理中');
      },
    });
  }, []);

  const handleAssignOpen = useCallback((record) => {
    setAssignTicket(record);
    setAssignTarget(null);
    setAssignModalVisible(true);
  }, []);

  const handleAssignConfirm = useCallback(() => {
    if (!assignTarget || !assignTicket) return;
    const target = handlerOptions.find((o) => o.value === assignTarget);
    setTickets((prev) => prev.map((t) =>
      t.key === assignTicket.key
        ? { ...t, status: 'processing', handlerId: assignTarget, handlerName: target?.name || assignTarget, handlerAccount: target?.account || '', updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19) }
        : t
    ));
    message.success('已分配');
    setAssignModalVisible(false);
    setAssignTicket(null);
    setAssignTarget(null);
  }, [assignTarget, assignTicket, handlerOptions]);

  const handleRelease = useCallback((record) => {
    Modal.confirm({
      title: '确认释放该工单？释放后其他客服可重新领取',
      onOk: () => {
        setTickets((prev) => prev.map((t) =>
          t.key === record.key
            ? { ...t, status: 'pending', handlerId: null, handlerName: null, handlerAccount: null, updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19) }
            : t
        ));
        message.success('工单已释放，退回待处理池');
      },
    });
  }, []);

  const handleNavigate = useCallback((ticketNo) => {
    navigate(`/customer/ticket-manage/detail/${ticketNo}`);
  }, [navigate]);

  const columns = [
    {
      title: '工单编号', dataIndex: 'ticketNo', key: 'ticketNo', width: 200, fixed: 'left',
      render: (text, record) => (
        <Button type="link" size="small" style={{ padding: 0, fontFamily: 'monospace' }}
          onClick={() => handleNavigate(record.ticketNo)}>
          {text}
        </Button>
      ),
    },
    {
      title: '标题', dataIndex: 'title', key: 'title', width: 260, ellipsis: true,
      render: (text) => <Tooltip title={text}>{text}</Tooltip>,
    },
    {
      title: '问题分类', dataIndex: 'category', key: 'category', width: 100,
      render: (c) => {
        const m = CATEGORY_MAP[c];
        return m ? <Tag color={m.color} style={{ border: 'none' }}>{m.label}</Tag> : c;
      },
    },
    {
      title: '优先级', dataIndex: 'priority', key: 'priority', width: 90,
      render: (p) => {
        const m = PRIORITY_MAP[p];
        return m ? <Tag color={m.color} style={{ border: 'none' }}>{m.label}</Tag> : p;
      },
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: (s) => {
        const m = STATUS_MAP[s];
        return m ? <Tag color={m.color} icon={m.icon} style={{ border: 'none' }}>{m.label}</Tag> : s;
      },
    },
    {
      title: '处理人', dataIndex: 'handlerName', key: 'handlerName', width: 130,
      render: (text, record) => text ? (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.handlerAccount}</Text>
        </div>
      ) : <span style={{ color: '#ccc' }}>-</span>,
    },
    {
      title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 180,
      sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
    },
    {
      title: '操作', key: 'action', fixed: 'right', width: 200,
      render: (_, record) => {
        const isPending = record.status === 'pending';
        const isProcessing = record.status === 'processing';
        if (readOnly) {
          return (
            <Button type="link" size="small" onClick={() => handleNavigate(record.ticketNo)}>详情</Button>
          );
        }
        return (
          <Space size={4}>
            {isPending && (
              <>
                <Button type="link" size="small" icon={<PlusOutlined />} onClick={() => handleClaim(record)}>领取</Button>
                <Button type="link" size="small" onClick={() => handleAssignOpen(record)}>分配</Button>
              </>
            )}
            {isProcessing && (
              <>
                <Button type="link" size="small" onClick={() => handleNavigate(record.ticketNo)}>处理</Button>
                <Button type="link" size="small" onClick={() => handleRelease(record)}>释放</Button>
              </>
            )}
            <Button type="link" size="small" onClick={() => handleNavigate(record.ticketNo)}>详情</Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      {/* 查询区 */}
      <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Form form={form} layout="inline" initialValues={{ status: 'all', category: 'all', priority: 'all' }}>
          <Form.Item name="status" style={{ marginBottom: 0 }}>
            <Select style={{ width: 120 }} allowClear placeholder="状态">
              <Option value="all">全部</Option>
              <Option value="pending">待处理</Option>
              <Option value="processing">处理中</Option>
              <Option value="resolved">已解决</Option>
              <Option value="closed">已关闭</Option>
            </Select>
          </Form.Item>
          <Form.Item name="category" style={{ marginBottom: 0 }}>
            <Select style={{ width: 120 }} allowClear placeholder="问题分类">
              <Option value="all">全部</Option>
              <Option value="techSupport">技术对接</Option>
              <Option value="bug">BUG</Option>
              <Option value="requirement">需求</Option>
              <Option value="other">其他</Option>
            </Select>
          </Form.Item>
          <Form.Item name="priority" style={{ marginBottom: 0 }}>
            <Select style={{ width: 100 }} allowClear placeholder="优先级">
              <Option value="all">全部</Option>
              <Option value="low">低</Option>
              <Option value="normal">普通</Option>
              <Option value="high">高</Option>
              <Option value="urgent">紧急</Option>
            </Select>
          </Form.Item>
          <Form.Item name="handlerId" style={{ marginBottom: 0 }}>
            <Select style={{ width: 140 }} allowClear placeholder="处理人" showSearch optionFilterProp="children">
              {handlerOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="keyword" style={{ marginBottom: 0 }}>
            <Input style={{ width: 200 }} placeholder="工单编号/标题，输入test可查询" allowClear />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 列表区 */}
      <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Table
          columns={columns}
          dataSource={filteredTickets}
          rowKey="key"
          scroll={{ x: 1300 }}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            defaultPageSize: 10,
            pageSizeOptions: ['10', '20', '50'],
          }}
          locale={{
            emptyText: (
              <div style={{ padding: 40 }}>
                <Text type="secondary">该企业暂无工单记录</Text>
              </div>
            ),
          }}
        />
      </Card>

      {/* 分配弹窗 */}
      <Modal
        title="分配工单"
        open={assignModalVisible}
        onOk={handleAssignConfirm}
        onCancel={() => setAssignModalVisible(false)}
        okText="确认分配"
        cancelText="取消"
      >
        <div style={{ marginBottom: 12 }}>
          <Text>工单编号：</Text>
          <Text code>{assignTicket?.ticketNo}</Text>
        </div>
        <Form.Item label="分配至" style={{ marginBottom: 0 }}>
          <Select
            placeholder="请选择客服"
            value={assignTarget}
            onChange={setAssignTarget}
            showSearch
            optionFilterProp="children"
            style={{ width: '100%' }}
          >
            {MOCK_AGENTS.map((agent) => (
              <Option key={agent.id} value={agent.id}>
                {agent.name}  {agent.account}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Modal>
    </div>
  );
};

export default CustomerTicketsTab;
